// @flow
import { pick, omit, snakeCase, toUpper } from 'lodash';
import type { State as AppState } from 'Common/types';
import type {
  SessionState,
  Cast,
  CastSenderToReceiverMessageType,
  AustinEvent,
} from 'Common/cast/types';
import type { SourceType } from 'Common/player/types';
import type { RecordingDetail } from 'Common/dvr/types';
import type { CastDetails } from 'Common/models/types';
import type { OpenAiring } from 'Common/guide/types';
import { AustinError } from 'Common/cast/types';

declare var cast: Cast;

export const RECEIVER_APP_PATH = '/chromecast/receiver/app';

export const DEFAULT_POSTER_URL = `https://${
  window.location.host
}${RECEIVER_APP_PATH}/no-image-available.jpg`;

export const isCastSessionActive = (sessionState: SessionState) =>
  sessionState === 'CAST_SESSION_STARTED' || sessionState === 'CAST_SESSION_RESUMED';

export const isCastSessionFailed = (sessionState: SessionState) =>
  sessionState === 'CAST_SESSION_START_FAILED' ||
  sessionState === 'CAST_SESSION_RESUME_FAILED' ||
  sessionState === 'CAST_SESSION_SUSPENDED';

export const shouldShowCastMiniController = (
  playerInFullscreen: boolean,
  sessionState: SessionState,
  error: string,
) =>
  !(
    playerInFullscreen ||
    sessionState === 'CAST_SESSION_DISCONNECTED' ||
    error === 'APPLICATION_NOT_RUNNING'
  );

export const isCorrectScreenForMiniController = (routeName?: string): boolean =>
  routeName === 'HomePage' ||
  routeName === 'DVRPage' ||
  routeName === 'GuidePage' ||
  routeName === 'OnDemandPage' ||
  routeName === 'WatchListPage' ||
  routeName === 'NetworksPage' ||
  routeName === 'NetworkPage' ||
  routeName === 'MyPurchasesPage' ||
  routeName === 'GamePage' ||
  routeName === 'FranchisePage' ||
  routeName === 'SearchPage' ||
  routeName === 'SearchResults' ||
  routeName === 'TeamsPage' ||
  routeName === 'TeamPage' ||
  routeName === 'QueuePage' ||
  routeName === 'TvRemotePage' ||
  routeName === 'PlayerPage' ||
  routeName === 'SlingPlayerPage' ||
  routeName === 'SportsPage';

/* Removes unnecessary for playback (and analytics) parts of payload in order to minimize payload size
   (maximum size of payload for custom chromecast messages is 64KB). */
export const optimizeSource = (source: string) => {
  const optimized = JSON.parse(source);

  if (optimized.listing && optimized.listing._franchise) {
    optimized.listing._franchise = pick(optimized.listing._franchise, [
      'run_time',
      'networks',
      'name',
      'content_identification',
      'genres',
      'releaseDate',
      'release_year',
      'kind',
      'rating',
      'nielsen_originator_call_letters',
      'franchiseName',
      'content_id',
      'slug',
    ]);
  }

  console.log('[CAST] Optimized source: ', optimized, JSON.stringify(optimized).length);

  return optimized;
};

export const pickDataForCastFromAppState = (
  appState: AppState,
  /* These are fields necessary for playback on the receiver's side */
  fields?: Array<string> = [
    'app',
    'est',
    'profiles',
    'cast',
    'networkInfo',
    'config',
    'device',
    'network',
    'auth',
    'franchise',
    ...(process.env.IS_BROWSER ? ['found'] : []) /* There is no "found" reducer in mobile. */,
  ],
) => {
  const state = pick(appState, fields);

  const currentFranchise = omit(
    state.franchise.franchiseDetails.bySlug[state.franchise.franchiseSlug],
    [
      'airings',
      'cast_and_crew',
      'episodesBySeason',
      'episode_seasons',
      'episodes',
      'season_filters',
      'timers',
    ],
  );

  const copy = {
    ...state,
    franchise: {
      ...state.franchise,
      bySlug: { [state.franchise.franchiseSlug]: currentFranchise },
      franchiseDetails: {
        ...state.franchise.franchiseDetails,
        bySlug: { [state.franchise.franchiseSlug]: currentFranchise },
      },
      recommendations: { fetching: false, error: false, content: [] },
    },
    ...(state.network
      ? /* Fully reset network state (it must be in place to prevent crashes but not necessary for playback). */
        { network: { ...state.network, content: { fetching: false, error: false, items: [] } } }
      : {}),
  };
  return copy;
};

export const generateSendStateCastMessage = (
  appState: AppState,
  fields?: Array<string>,
): CastSenderToReceiverMessageType => {
  const castState = pickDataForCastFromAppState(appState, fields);
  return { type: 'RESET_STORE', payload: castState };
};

export const getSlingConfig = (state: AppState, action: Object) => {
  const {
    app: { activeReceiver: { finderId, slingAdmin } = {} },
  } = state;

  if (action.type === 'SET_DVR_STREAM') {
    const { sourceType, dvrId, dvrProgress } = action;

    return { finderId, slingAdmin, sourceType, dvrId, dvrProgress };
  }

  if (action.type === 'SET_LIVE_STREAM') {
    const {
      sourceType,
      channel: { channel: channelId, service_unique_id: serviceUniqueId },
    } = action;

    return { finderId, slingAdmin, sourceType, channelId, serviceUniqueId };
  }

  console.error(`[CAST] Can't pull Austin configuration from non sling action: `, action);
  return { finderId, slingAdmin };
};

export const getCastDetails = (
  sourceType: SourceType,
  recording?: RecordingDetail,
  openAiring: OpenAiring,
  franchise: Object,
  airing: Object,
): CastDetails => {
  switch (sourceType) {
    case 'on_demand':
    case 'trailer':
    case 'est': {
      const { name = '', images: { poster_url: posterUrl = '' } = {}, slug } = franchise;
      return {
        title: name,
        subtitle: '',
        poster: posterUrl,
        slug,
      };
    }
    case 'live_linear':
    case 'live': {
      const {
        name,
        episode_title: episodeTitle,
        images: { poster_url: posterUrl } = {},
        slug,
      } = openAiring;
      const {
        name: airingName,
        episode_title: airingEpisodeTitle,
        images: { poster_url: airingPosterUrl } = {},
        slug: airingSlug,
      } = airing;
      return {
        title: name || airingName || '',
        subtitle: episodeTitle || airingEpisodeTitle || '',
        poster: posterUrl || airingPosterUrl || '',
        slug: slug || airingSlug || '',
      };
    }
    case 'dvr':
      return recording
        ? {
            title: recording.title,
            subtitle: recording.event_name,
            poster: recording.program_logo,
            slug: recording.slug,
          }
        : { title: '', subtitle: '', poster: '', slug: '' };
    default:
      throw new Error(
        `getCastDetails got called with a wrong source type: ${sourceType}.
        It allows only one of these: 'est','live' or 'dvr'.`,
      );
  }
};

export const getCastReceiverContext = () => cast.framework.CastReceiverContext.getInstance();
export const getSenders = () => getCastReceiverContext().getSenders();
export const getPlayerManager = () => getCastReceiverContext().getPlayerManager();

export const getCastContext = () => cast.framework.CastContext.getInstance();
export const getCastSession = () => getCastContext().getCurrentSession();
export const getCurrentMediaSession = () => getCastSession().getMediaSession();
export const endCastSession = () => {
  const session = getCastSession();
  if (session) session.endSession(true); /* End session and close receiver app. */
};

export const sleep = (timeout: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, timeout));

export const AUSTIN_EVENTS: Array<$PropertyType<AustinEvent, 'type'>> = [
  'onError',
  'onInfo',
  'onPause',
  'onPopUp',
  'onReady',
  'onStallEnd',
  'onStallStart',
  'onStatusChange',
  'onTimeUpdate',
  'onPlaying',
];

/* Generates messages for debug info widget. */
export const serializeAustinEvent = (event: AustinEvent): string => {
  let msg = '';

  if (event.type === 'onStatusChange') msg = event.payload;
  else if (event.type === 'onPopUp') msg = `${event.type}_${event.payload.type}`;
  else if (event.type === 'onStallStart') msg = `${event.type} ${event.payload.type}`;
  else if (event.type === 'onStallEnd') msg = `${event.type} ${event.payload.type}`;
  else if (event.type === 'onReady') msg = `${event.type} ${event.payload.type}`;
  else if (event.type === 'onInfo') msg = `${event.type} ${event.payload.reason}`;
  else if (event.type === 'onPause') msg = event.type;
  else if (event.type === 'onError') {
    const { payload } = event;
    if (payload instanceof AustinError) {
      msg = `${payload.type} ${payload.message}`;
    } else {
      const { info, reason } = payload;
      if (typeof info.code === 'number') msg = `${event.type} ${info.code} ${info.info || ''}`;
      else msg = `${event.type} ${reason || ''}`;
    }
  } else if (event.type === 'onTimeUpdate') msg = 'onTimeUpdate';
  else if (event.type === 'onPlaying') msg = 'onPlaying';
  else if (event.type === 'configure') msg = 'configureAustinRequest';
  else (event.type: empty); // eslint-disable-line no-unused-expressions

  return toUpper(snakeCase(msg));
};

const ERROR_TYPE_TO_ERROR_MESSAGE_MAPPING: Map<string, string> = new Map([
  ['connectFailed', 'Connection to the box has failed'],
  ['boxNotReady', 'Box is not ready for streaming'],
  ['streamLost', 'Stream from the box has lost'],
  ['connectionLost', 'Connection to the box has lost'],
  ['playerError', 'Unexpected player error'],
  ['forceTransfer', 'Stream from the box has been taken over'],
  ['fail_to_bootstrap', 'Fail to initiate box connection'],
  ['fail_to_connect', 'Fail to connect to the box'],
  ['fail_to_play_stream', 'Fail to play the stream'],
]);

export const mapAustinErrorToErrorMessage = (error?: string = '') => {
  const errorMessage =
    ERROR_TYPE_TO_ERROR_MESSAGE_MAPPING.get(error) || 'An unknown error has occurred';

  return `${errorMessage}. Please try again later.`;
};

/* This is the only way to check that host is a Chromecast device (https://issuetracker.google.com/issues/36189456) */
export const isRunningOnChromeCastDevice = () => window.navigator.userAgent.indexOf('CrKey') !== -1;
